// What this device remembers about the person sitting at it (D-33, MAJLIS-42 point 2): the name, the
// speaking language, the reading language and who is on the phone.
//
// Why it exists. The owner's seat joined speaking English because the entry screen took its languages from
// his iPhone's language list, and he then spoke Arabic for a whole session into the wrong model. So: a
// stored choice WINS over the device's languages, and `deviceDefaults` is consulted only for a field that
// has nothing stored. App.tsx is where that precedence is written (`stored.speak ?? defaults.speak`) and
// entry.test.tsx is where it is held, with the cleared-storage fallback beside it as the control.
//
// A value that is not in today's lists is treated as nothing stored, so a list that shrinks cannot leave a
// seat pointing at a language the engine no longer takes.
import { READ_LANGUAGES } from "./data/readLanguages";
import { SPEAK_LANGUAGES, type Language } from "./data/speakLanguages";
import type { KeyValue } from "./protocol/identity";
import type { SeatKind } from "./protocol/types";

export const PREF_KEYS = {
  name: "majlis.name",
  speak: "majlis.speak",
  read: "majlis.read",
  seatKind: "majlis.seat-kind",
  // MAJLIS-64 (D-43(2), UI-C21): the personal setting for the live voices while a translated phrase is spoken
  liveUnder: "majlis.live-under",
} as const;

export type PrefKey = keyof typeof PREF_KEYS;

export interface StoredPrefs {
  /** "" when nothing is stored: the name field starts empty, as it always has. */
  name: string;
  speak: string | null;
  read: string | null;
  seatKind: SeatKind | null;
  /** D-43(2): live voices under a spoken translation - `lowered` (the default, also when nothing is stored) or `muted`
   * (for noisy places). A personal setting kept on this phone only; iOS mutes whatever it says (volume is ignored). */
  liveUnder: LiveUnder;
}

export type LiveUnder = "lowered" | "muted";

const SEAT_KINDS: readonly SeatKind[] = ["personal", "shared"];

function get(storage: KeyValue | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null; // storage refused (private mode, blocked site data): nothing is remembered
  }
}

const known = (list: readonly Language[], value: string | null): string | null =>
  value !== null && list.some((l) => l.tag === value) ? value : null;

export function readPrefs(storage: KeyValue | null): StoredPrefs {
  const seatKind = get(storage, PREF_KEYS.seatKind);
  return {
    name: get(storage, PREF_KEYS.name) ?? "",
    speak: known(SPEAK_LANGUAGES, get(storage, PREF_KEYS.speak)),
    read: known(READ_LANGUAGES, get(storage, PREF_KEYS.read)),
    seatKind: SEAT_KINDS.find((k) => k === seatKind) ?? null,
    liveUnder: get(storage, PREF_KEYS.liveUnder) === "muted" ? "muted" : "lowered",
  };
}

/** Remember one choice. A storage that refuses is not an error: the app simply forgets. */
export function remember(storage: KeyValue | null, key: PrefKey, value: string): void {
  try {
    storage?.setItem(PREF_KEYS[key], value);
  } catch {
    // storage refused: nothing to remember
  }
}
